import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  WebhookDeliveryDetailDto,
  WebhookDeliveryDto,
  WebhookDeliveryStatus,
  WebhookEndpointDetailDto,
  WebhookEndpointDto,
  WebhookEventDescriptor,
  WebhookSecretRevealDto,
  WebhookSettingsDto,
} from '@cpm/shared';
import { Copy, KeyRound, Pencil, Play, Plus, RotateCcw, Send, Trash2, Webhook } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useMe } from '../api/auth.js';
import { api } from '../api/client.js';
import { Modal } from '../components/Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Spinner,
  type BadgeTone,
} from '../components/ui.js';

const DELIVERY_TONE: Record<WebhookDeliveryStatus, BadgeTone> = {
  delivered: 'green',
  pending: 'gray',
  failed: 'yellow',
  dead: 'red',
};

function fmt(ts: string | null): string {
  return ts ? new Date(ts).toLocaleString() : '—';
}

// ── Secret reveal (shown once) ───────────────────────────────────────

function SecretReveal({ secret, onClose }: { secret: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal title="Signing secret" open onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Store this signing secret now — it is shown once and never again. Your receiver uses it to
          verify the <code className="font-mono">X-CPM-Webhook-Signature</code> header.
        </p>
        <div className="flex items-center gap-2">
          <Input readOnly value={secret} className="font-mono text-xs" />
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(secret);
              setCopied(true);
            }}
          >
            <Copy className="size-4" /> {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <div className="flex justify-end">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Create / edit form ───────────────────────────────────────────────

function EndpointForm({
  existing,
  onDone,
  onSecret,
}: {
  existing?: WebhookEndpointDto;
  onDone: () => void;
  onSecret?: (secret: string) => void;
}) {
  const queryClient = useQueryClient();
  const catalog = useQuery({
    queryKey: ['webhooks', 'event-types'],
    queryFn: () => api.get<WebhookEventDescriptor[]>('/api/webhooks/event-types'),
  });
  const [url, setUrl] = useState(existing?.url ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [types, setTypes] = useState<Set<string>>(new Set(existing?.eventTypes ?? []));
  const [maxAttempts, setMaxAttempts] = useState(String(existing?.maxAttempts ?? 8));
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);

  const grouped = useMemo(() => {
    const groups = new Map<string, WebhookEventDescriptor[]>();
    for (const d of catalog.data ?? []) {
      const list = groups.get(d.group) ?? [];
      list.push(d);
      groups.set(d.group, list);
    }
    return [...groups.entries()];
  }, [catalog.data]);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        url,
        description: description || null,
        eventTypes: [...types],
        maxAttempts: Number(maxAttempts),
        enabled,
      };
      if (existing) return api.patch<WebhookEndpointDto>(`/api/webhooks/endpoints/${existing.id}`, body);
      return api.post<WebhookSecretRevealDto>('/api/webhooks/endpoints', body);
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['webhooks'] });
      if (!existing && result && 'secret' in result) onSecret?.(result.secret);
      onDone();
    },
  });

  function toggle(type: string) {
    setTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="space-y-4"
    >
      <div>
        <Label htmlFor="wh-url">Endpoint URL</Label>
        <Input
          id="wh-url"
          required
          type="url"
          placeholder="https://billing.example.com/webhooks/cpm"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="wh-desc">Description</Label>
        <Input
          id="wh-desc"
          placeholder="e.g. Production billing system"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <div>
        <Label>Subscribed events</Label>
        {catalog.isPending ? (
          <Spinner label="Loading event types…" />
        ) : (
          <div className="max-h-64 space-y-3 overflow-y-auto rounded-lg ring-1 ring-slate-200 p-3 dark:ring-slate-700">
            {grouped.map(([group, items]) => (
              <div key={group}>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {group}
                </p>
                <div className="space-y-1">
                  {items.map((d) => (
                    <label key={d.type} className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={types.has(d.type)}
                        onChange={() => toggle(d.type)}
                      />
                      <span>
                        <span className="font-mono text-xs">{d.type}</span>
                        <span className="block text-xs text-slate-500 dark:text-slate-400">
                          {d.description}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-4">
        <div className="w-32">
          <Label htmlFor="wh-max">Max attempts</Label>
          <Input
            id="wh-max"
            type="number"
            min={1}
            max={20}
            value={maxAttempts}
            onChange={(e) => setMaxAttempts(e.target.value)}
          />
        </div>
        <label className="mt-7 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Enabled
        </label>
      </div>

      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" loading={save.isPending} disabled={types.size === 0}>
          {existing ? 'Save changes' : 'Create endpoint'}
        </Button>
      </div>
    </form>
  );
}

// ── Delivery inspection ──────────────────────────────────────────────

function DeliveryInspect({ deliveryId }: { deliveryId: string }) {
  const detail = useQuery({
    queryKey: ['webhooks', 'delivery', deliveryId],
    queryFn: () => api.get<WebhookDeliveryDetailDto>(`/api/webhooks/deliveries/${deliveryId}`),
  });
  if (detail.isPending) return <Spinner label="Loading…" />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  return (
    <div className="grid gap-3 p-4 sm:grid-cols-2">
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Payload</p>
        <pre className="max-h-56 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-200">
          {JSON.stringify(detail.data.payload, null, 2)}
        </pre>
      </div>
      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Response</p>
        <div className="space-y-1 text-sm">
          <p>
            Status:{' '}
            <span className="font-mono">{detail.data.delivery.responseStatus ?? '—'}</span>
          </p>
          <p>
            Duration:{' '}
            <span className="font-mono">
              {detail.data.delivery.responseDurationMs != null
                ? `${detail.data.delivery.responseDurationMs} ms`
                : '—'}
            </span>
          </p>
          {detail.data.delivery.lastError && (
            <p className="text-red-600 dark:text-red-400">{detail.data.delivery.lastError}</p>
          )}
        </div>
        <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-slate-950 p-3 text-xs text-slate-200">
          {detail.data.responseBody ?? '(no response body)'}
        </pre>
      </div>
    </div>
  );
}

function EndpointDeliveries({ endpointId, isAdmin }: { endpointId: string; isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [inspecting, setInspecting] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ['webhooks', 'endpoint', endpointId],
    queryFn: () => api.get<WebhookEndpointDetailDto>(`/api/webhooks/endpoints/${endpointId}`),
  });
  const redeliver = useMutation({
    mutationFn: (id: string) => api.post(`/api/webhooks/deliveries/${id}/redeliver`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  if (detail.isPending) return <Spinner label="Loading deliveries…" />;
  if (detail.error) return <ErrorNote error={detail.error} />;
  if (detail.data.deliveries.length === 0)
    return <p className="p-4 text-sm text-slate-500 dark:text-slate-400">No deliveries yet.</p>;

  return (
    <div className="border-t border-slate-100 dark:border-slate-800">
      <table className="w-full text-left text-sm">
        <thead className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          <tr>
            <th className="px-4 py-2 font-medium">Event</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Attempts</th>
            <th className="px-4 py-2 font-medium">Response</th>
            <th className="px-4 py-2 font-medium">When</th>
            <th className="px-4 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {detail.data.deliveries.map((d: WebhookDeliveryDto) => (
            <Fragment key={d.id}>
              <tr
                className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                onClick={() => setInspecting(inspecting === d.id ? null : d.id)}
              >
                <td className="px-4 py-2 font-mono text-xs">{d.eventType}</td>
                <td className="px-4 py-2">
                  <Badge tone={DELIVERY_TONE[d.status]}>{d.status}</Badge>
                </td>
                <td className="px-4 py-2">
                  {d.attempts}/{d.maxAttempts}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{d.responseStatus ?? '—'}</td>
                <td className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
                  {fmt(d.createdAt)}
                </td>
                <td className="px-4 py-2 text-right">
                  {isAdmin && (d.status === 'failed' || d.status === 'dead') && (
                    <Button
                      variant="secondary"
                      className="px-2 py-1 text-xs"
                      loading={redeliver.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        redeliver.mutate(d.id);
                      }}
                    >
                      <RotateCcw className="size-3.5" /> Redeliver
                    </Button>
                  )}
                </td>
              </tr>
              {inspecting === d.id && (
                <tr>
                  <td colSpan={6} className="p-0">
                    <DeliveryInspect deliveryId={d.id} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      {redeliver.error != null && (
        <div className="p-3">
          <ErrorNote error={redeliver.error} />
        </div>
      )}
    </div>
  );
}

// ── Usage-snapshot schedule card ─────────────────────────────────────

function SnapshotCard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['webhooks', 'settings'],
    queryFn: () => api.get<WebhookSettingsDto>('/api/webhooks/settings'),
  });
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [hours, setHours] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.patch<WebhookSettingsDto>('/api/webhooks/settings', {
        usageSnapshotEnabled: enabled ?? settings.data?.usageSnapshotEnabled ?? true,
        usageSnapshotIntervalHours: Number(hours ?? settings.data?.usageSnapshotIntervalHours ?? 24),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['webhooks', 'settings'] }),
  });
  const snapshotNow = useMutation({
    mutationFn: () => api.post<{ published: number }>('/api/webhooks/snapshot'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  const enabledValue = enabled ?? settings.data?.usageSnapshotEnabled ?? true;
  const hoursValue = hours ?? String(settings.data?.usageSnapshotIntervalHours ?? 24);

  return (
    <Card>
      <CardHeader
        title="Usage snapshots"
        description="Periodically publish a usage.snapshot per subscription (disk, memory, resource counts). Only generated when an enabled endpoint subscribes."
        actions={
          isAdmin && (
            <Button
              variant="secondary"
              loading={snapshotNow.isPending}
              onClick={() => snapshotNow.mutate()}
            >
              <Play className="size-4" /> Snapshot now
            </Button>
          )
        }
      />
      <div className="space-y-4 p-5">
        {settings.isPending ? (
          <Spinner />
        ) : settings.error ? (
          <ErrorNote error={settings.error} />
        ) : (
          <>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={enabledValue}
                  disabled={!isAdmin}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
                Enabled
              </label>
              <div className="w-40">
                <Label htmlFor="wh-hours">Interval (hours)</Label>
                <Input
                  id="wh-hours"
                  type="number"
                  min={1}
                  max={8760}
                  disabled={!isAdmin}
                  value={hoursValue}
                  onChange={(e) => setHours(e.target.value)}
                />
              </div>
              {isAdmin && (
                <Button loading={save.isPending} onClick={() => save.mutate()}>
                  Save
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Last snapshot: {fmt(settings.data.lastSnapshotAt)}
              {snapshotNow.data ? ` · published ${snapshotNow.data.published}` : ''}
            </p>
            {save.error != null && <ErrorNote error={save.error} />}
            {snapshotNow.error != null && <ErrorNote error={snapshotNow.error} />}
          </>
        )}
      </div>
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────────────────

export function WebhooksPage() {
  const queryClient = useQueryClient();
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<WebhookEndpointDto | null>(null);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [pingResult, setPingResult] = useState<string | null>(null);

  const endpoints = useQuery({
    queryKey: ['webhooks', 'endpoints'],
    queryFn: () => api.get<WebhookEndpointDto[]>('/api/webhooks/endpoints'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/webhooks/endpoints/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });
  const rotate = useMutation({
    mutationFn: (id: string) =>
      api.post<WebhookSecretRevealDto>(`/api/webhooks/endpoints/${id}/rotate-secret`),
    onSuccess: (result) => setRevealed(result.secret),
  });
  const ping = useMutation({
    mutationFn: (id: string) => api.post<WebhookDeliveryDto>(`/api/webhooks/endpoints/${id}/ping`),
    onSuccess: (result) => {
      setPingResult(
        result.status === 'delivered'
          ? `Ping delivered (HTTP ${result.responseStatus}).`
          : `Ping ${result.status}: ${result.lastError ?? 'no response'}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
  const dispatch = useMutation({
    mutationFn: () => api.post('/api/webhooks/dispatch'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['webhooks'] }),
  });

  return (
    <div>
      <PageHeader
        title="Billing Webhooks"
        description="Signed outbound webhooks any billing system can consume. Register an endpoint, subscribe it to events, and verify deliveries with its per-endpoint secret."
        actions={
          isAdmin && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                loading={dispatch.isPending}
                onClick={() => dispatch.mutate()}
              >
                <Send className="size-4" /> Dispatch now
              </Button>
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="size-4" /> New endpoint
              </Button>
            </div>
          )
        }
      />

      {pingResult && (
        <div className="mb-4">
          <div className="rounded-lg bg-sky-50 px-4 py-3 text-sm text-sky-800 ring-1 ring-inset ring-sky-600/10 dark:bg-sky-500/10 dark:text-sky-300">
            {pingResult}
          </div>
        </div>
      )}
      {(remove.error ?? rotate.error ?? ping.error) != null && (
        <div className="mb-4">
          <ErrorNote error={remove.error ?? rotate.error ?? ping.error} />
        </div>
      )}

      <div className="space-y-4">
        <SnapshotCard isAdmin={isAdmin} />

        <Card>
          <CardHeader
            title="Endpoints"
            description="Each endpoint has its own signing secret. Expand a row to inspect delivery history and responses."
          />
          {endpoints.isPending ? (
            <Spinner />
          ) : endpoints.error ? (
            <div className="p-5">
              <ErrorNote error={endpoints.error} />
            </div>
          ) : endpoints.data.length === 0 ? (
            <EmptyState
              icon={<Webhook className="size-6" />}
              title="No webhook endpoints"
              description="Register an endpoint to start delivering signed events to your billing system."
              action={
                isAdmin && (
                  <Button onClick={() => setCreateOpen(true)}>
                    <Plus className="size-4" /> New endpoint
                  </Button>
                )
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3 font-medium">URL</th>
                    <th className="px-5 py-3 font-medium">Events</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Deliveries</th>
                    <th className="px-5 py-3 font-medium">Last</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {endpoints.data.map((e) => (
                    <Fragment key={e.id}>
                      <tr
                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                        onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                      >
                        <td className="px-5 py-3">
                          <div className="font-mono text-xs">{e.url}</div>
                          {e.description && (
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {e.description}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <Badge tone="blue">{e.eventTypes.length}</Badge>
                        </td>
                        <td className="px-5 py-3">
                          <Badge tone={e.enabled ? 'green' : 'gray'}>
                            {e.enabled ? 'enabled' : 'disabled'}
                          </Badge>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(
                              Object.entries(e.deliveryCounts) as Array<
                                [WebhookDeliveryStatus, number]
                              >
                            )
                              .filter(([, n]) => n > 0)
                              .map(([status, n]) => (
                                <Badge key={status} tone={DELIVERY_TONE[status]}>
                                  {n} {status}
                                </Badge>
                              ))}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-xs text-slate-500 dark:text-slate-400">
                          {fmt(e.lastDeliveryAt)}
                        </td>
                        <td className="px-5 py-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                          {isAdmin && (
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                className="px-2 py-1 text-xs"
                                loading={ping.isPending && ping.variables === e.id}
                                onClick={() => ping.mutate(e.id)}
                                title="Send a signed test ping"
                              >
                                <Send className="size-3.5" /> Ping
                              </Button>
                              <Button
                                variant="ghost"
                                className="px-2 py-1 text-xs"
                                onClick={() => setEditing(e)}
                              >
                                <Pencil className="size-3.5" /> Edit
                              </Button>
                              <Button
                                variant="ghost"
                                className="px-2 py-1 text-xs"
                                loading={rotate.isPending && rotate.variables === e.id}
                                onClick={() => rotate.mutate(e.id)}
                                title="Rotate signing secret"
                              >
                                <KeyRound className="size-3.5" /> Rotate
                              </Button>
                              <Button
                                variant="ghost"
                                className="px-2 py-1 text-xs text-red-600"
                                onClick={() => {
                                  if (window.confirm(`Delete webhook endpoint ${e.url}?`))
                                    remove.mutate(e.id);
                                }}
                              >
                                <Trash2 className="size-3.5" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {expanded === e.id && (
                        <tr>
                          <td colSpan={6} className="p-0">
                            <EndpointDeliveries endpointId={e.id} isAdmin={isAdmin} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Modal title="New webhook endpoint" open={createOpen} onClose={() => setCreateOpen(false)} wide>
        <EndpointForm onDone={() => setCreateOpen(false)} onSecret={(s) => setRevealed(s)} />
      </Modal>
      <Modal
        title={editing ? `Edit ${editing.url}` : 'Edit endpoint'}
        open={editing !== null}
        onClose={() => setEditing(null)}
        wide
      >
        {editing && <EndpointForm existing={editing} onDone={() => setEditing(null)} />}
      </Modal>
      {revealed && <SecretReveal secret={revealed} onClose={() => setRevealed(null)} />}
    </div>
  );
}
